import { v2_useCustomerResetPasswordRequest } from '@crocodille/common';
import { AppPage } from '@framework/page';
import PageMeta from '@framework/page-meta';
import { getAppLayoutWithCart } from '@layouts/app-layout/app-layout';
import { getApp } from '@utils/app';
import { getServerSideTranslationProps } from '@utils/i18n';
import { GetServerSideProps } from 'next';
import Link from 'next/link';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AccountPasswordResetConfirmation from '../../../components/account/account-password-reset-confirmation/account-password-reset-confirmation';
import AccountPasswordResetLayout from '../../../components/account/account-password-reset-layout/account-password-reset-layout';
import Button from '../../../components/elements/button/button';
import Error from '../../../components/elements/error/error';
import InputEnvelope from '../../../components/elements/input-envelope/input-envelope';
import Input from '../../../components/elements/input-2/input-2';
import SpinnerIcon from '../../../components/icons/spinner-icon';
import styles from './password-reset.module.scss';

import * as routing from '../../../routing';

const PasswordResetInitPage: AppPage = () => {
  const { t } = useTranslation();

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isMailEmpty, setIsMailEmpty] = useState(false);

  const { trigger, resetPasswordError, isMutating } =
    v2_useCustomerResetPasswordRequest({
      onSuccess: () => setShowConfirmation(true),
    });

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const email = new FormData(e.currentTarget).get('email');

      if (typeof email !== 'string') {
        return;
      }

      if (email?.length === 0) {
        setIsMailEmpty(true);
      } else {
        trigger({ email });
      }
    },
    [trigger],
  );

  return (
    <>
      <PageMeta pageTitle={t('password_reset.page_title')} description={''} />
      <AccountPasswordResetLayout>
        {showConfirmation ? (
          <>
            <AccountPasswordResetConfirmation>
              {t('password_reset.confirmation')}
            </AccountPasswordResetConfirmation>
            <div>
              <Link
                href={routing.ACCOUNT_LOGIN}
                legacyBehavior
                prefetch={false}
              >
                <Button as="a" display="inline" className={styles.loginBtn}>
                  {t('account.login')}
                </Button>
              </Link>
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <InputEnvelope
              label={t('password_reset.email')}
              required
              noLabelElement
              error={isMailEmpty ? t('validation_error.required') : null}
            >
              <Input
                name="email"
                type="email"
                autoComplete="email"
                placeholder={t('password_reset.email')}
              />
            </InputEnvelope>
            {resetPasswordError ? (
              <Error>{t(`password_reset.error.${resetPasswordError}`)}</Error>
            ) : null}
            <Button
              type="submit"
              disabled={isMutating}
              round
              className={styles.submitBtn}
            >
              {t('password_reset.submit')}
              <Button.Icon>{isMutating ? <SpinnerIcon /> : null}</Button.Icon>
            </Button>
          </form>
        )}
      </AccountPasswordResetLayout>
    </>
  );
};

PasswordResetInitPage.getLayout = (page) => {
  return getAppLayoutWithCart(page);
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const app = getApp(context);

  const translationProps = await getServerSideTranslationProps(app, 'messages');

  return {
    props: {
      ...translationProps,
    },
  };
};

export default PasswordResetInitPage;
